import * as d3 from 'd3'

import { degreesToRadians, formatPoint, calcMiddlePoint } from '@aytm/app/stats/page/data-centrifuge/lib/utils'

// D3 key accessor functions. Keys deliberately exclude x/y: a node that only moves keeps
// its key, so it lands in the UPDATE selection and is repositioned by animateSVG's
// transition instead of being exit+entered (removed and redrawn). `status` stays in the
// vector key because a status change must rebuild the vector (gradient / icon / spin).
export const getD = d => d
export const getKeys = d => d && d.id

// Color functions
export const getVectorColor = d => (['idle', 'waiting', 'error'].includes(d.status) ? '#ffffff' : d.color)

export const getVectorDarkerColor = d =>
  ['idle', 'waiting', 'error'].includes(d.status) ? '#ffffff' : d3.rgb(d.color).darker()

export const getVectorStrokeColor = (d, selectedVector) => {
  if (['idle', 'waiting', 'error'].includes(d.status)) return '#ffffff'
  if (['running'].includes(d.status)) return `url(#group-vector-${d.id}-gradient)`
  if (selectedVector != null && selectedVector != d.id) return '#a0a0a080'
  return d.color
}

export const getVectorStrokeWidth = d => (['running'].includes(d.status) ? 5 : 2)

export const getTextColor = (d, selectedVector) => {
  if (['idle', 'waiting', 'error'].includes(d.status)) return '#ffffff'
  if (selectedVector != null && selectedVector != d.id) return '#a0a0a080'
  return d.color
}

export const getTextOpacity = (d, status) => (d && (!status || ['running', 'done'].includes(status)) ? 1 : 0)

export const getIconColor = (d, selectedVector) => {
  if (['idle', 'waiting', 'error'].includes(d.status)) return '#aeaeae'
  if (selectedVector != null && selectedVector != d.id) return '#a0a0a080'
  return d.color
}

export const getIconOpacity = (d, status) => (d || status ? 1 : 0)

export const getCircleColor = (d, selectedVector) => {
  if (selectedVector != null) {
    const v = d.vectors.find(_v => selectedVector == _v.id)
    return d.vectors.length > 1 ? (v && v.color) || '#a0a0a080' : '#ffffff'
  }
  return '#ffffff'
}

export const getLinkColor = (d, selectedVector) =>
  selectedVector != null && selectedVector != d.vector.id ? '#a0a0a0c4' : d.color

export const getLinkOpacity = (d, data, opacity, selectedVector) => {
  let _opacity
  if (data.vectors.length > 2 && d.score.vectors.length == 2 && d.weight > 0.9) {
    _opacity = 0.75 * Math.min(1, Math.max(0, d.weight))
  } else if (data.vectors.length > 1 && d.score.vectors.length > 1) {
    const k = opacity.multi.coef / 1350
    const b = 1 - 300 * k
    _opacity = Math.pow(
      opacity.multi.min + opacity.multi.power * Math.min(1, Math.max(0, d.weight)),
      k * data.totals.scores.all.length + b
    )
  } else {
    _opacity = opacity.single.min + opacity.single.power * Math.min(1, Math.max(0, d.weight))
  }

  if (selectedVector != null) {
    return selectedVector != d.vector.id ? _opacity / 2 : Math.min(1, 1.5 * _opacity)
  }
  return _opacity
}

// D3 line generator for links
export const getLinkCurve = d3
  .line()
  .x(d => d.x)
  .y(d => d.y)
  .curve(d3.curveBasis)

// D3 arc generator for scores
export const getScoreArc = d3
  .arc()
  .innerRadius((d, stroke = 3) => {
    const s = d.r - stroke > 3 ? stroke : stroke - 1
    const r = d.r - stroke > 3 ? d.r : d.r - 1
    return (d.color || d.colors.length > 1) && s < r ? r - s : 0
  })
  .outerRadius((d, stroke = 3) => (d.r - stroke > 3 ? d.r : d.r - 1))
  .startAngle(d => degreesToRadians(d.start + 90))
  .endAngle(d => degreesToRadians(d.end + 90))

// A score row can be absent while the arc it belongs to is still mounted — the layout drops a
// response before the D3 join removes its node — so the row lookup is guarded like the vector one.
export const getScoreArcData = (d, data, selectedVector) => {
  if (selectedVector != null) {
    const s = data.scores.find(_s => _s.id == d.score)
    const v = s?.vectors.find(_v => selectedVector == _v.id)
    return { ...d, colors: [(v && v.color) || '#a0a0a080'] }
  }
  return d
}

export const getScoreGradient = (d, data, node, selectedVector) => {
  if (selectedVector != null) {
    const s = data.scores.find(_s => _s.id == d.score)
    const v = s?.vectors.find(_v => selectedVector == _v.id)
    return (v && v.color) || '#a0a0a080'
  }
  return d.colors[d.colors.length > 1 ? 1 : 0]
}

// Path helpers
// eslint-disable-next-line max-params
export const getArcPath = (rx, ry, xAxisRotation, largeArcFlag, sweepFlag, x, y) =>
  [rx, ',', ry, ' ', xAxisRotation, ' ', largeArcFlag, ',', sweepFlag, ' ', x, ',', y].join('')

// eslint-disable-next-line max-params
export const getRectPath = (x, y, width, height, tr, br, bl, tl) => {
  const data = []

  data.push('M' + (x + width / 2) + ',' + y)
  data.push('H' + (x + width - tr))
  if (tr > 0) data.push('A' + getArcPath(tr, tr, 0, 0, 1, x + width, y + tr))
  data.push('V' + (y + height - br))
  if (br > 0) data.push('A' + getArcPath(br, br, 0, 0, 1, x + width - br, y + height))
  data.push('H' + (x + bl))
  if (bl > 0) data.push('A' + getArcPath(bl, bl, 0, 0, 1, x + 0, y + height - bl))
  data.push('V' + (y + tl))
  if (tl > 0) data.push('A' + getArcPath(tl, tl, 0, 0, 1, x + tl, y + 0))
  data.push('Z')

  return data.join(' ')
}

export const getCirclePath = (x, y, r) =>
  `M ${x} ${y - r} a ${r},${r} 0 1,0 0, ${2 * r} a ${r},${r} 0 1,0 0, -${2 * r} Z`

// Generate link path between points
export const generateLinkPath = (d, data) => {
  const points = d.start.map(p => formatPoint(p, data.angle))
  return getLinkCurve(points)
}

// Previous-frame counterpart of a datum, so enter animations start from where the node was;
// falls back to the datum itself when it had no previous frame.
export const getOldD = (d, old) => old.find(v => v.id == d.id) || d

// Generate vector link path
export const generateVectorLinkPath = (d, oldVectors, oldAngle) => {
  const oldD = getOldD(d, oldVectors)
  const p = formatPoint(oldD, oldAngle)
  const m = calcMiddlePoint(
    [
      { x: -p.x, y: -p.y },
      { x: 0, y: 0 }
    ],
    30
  )
  return getLinkCurve([{ x: -p.x, y: -p.y }, m, { x: 0, y: 0 }])
}
